import { faVideo, faVideoSlash } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { secondsToMilliseconds } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Text, Tooltip } from '@growtherapy/sprout-ui';

import { ControlPanelButton } from '../../components';
import { toast } from '../../toasts';
import {
  LocalMediaStatus,
  localVideoContextAtom,
  useStartVideo,
  useStopVideo,
  useStoredAudioVideoSettings,
} from '../../video-sdk';
import { NoAccessButton } from './NoAccess';

const NO_ACCESS_TOAST_KEY = 'no-access-camera';

export function VideoControl({
  isEnabled,
  loading,
  onToggle,
}: {
  isEnabled?: boolean;
  loading?: boolean;
  onToggle: (enable: boolean) => void;
}) {
  const t = useTranslate();
  const label = isEnabled ? t('telehealth-core:controls.toggleVideoOff') : t('telehealth-core:controls.toggleVideoOn');

  return (
    <Tooltip childIsInteractive text={label}>
      <ControlPanelButton
        aria-label={label}
        loading={loading}
        className={classNames({ 'text-coral-600': !isEnabled })}
        iconDefinition={isEnabled ? faVideo : faVideoSlash}
        data-testid='video-control'
        onClick={() => onToggle(!isEnabled)}
      />
    </Tooltip>
  );
}

export function VideoControlWrapper({ openHelpModal }: { openHelpModal?: () => void }) {
  const t = useTranslate();
  const { delegator: localVideoDelegator, status: localVideoStatus } = useAtomValue(localVideoContextAtom);
  const startVideo = useStartVideo();
  const stopVideo = useStopVideo();
  const isLoading = localVideoStatus === LocalMediaStatus.LOADING;
  const isEnabled = localVideoDelegator && localVideoStatus === LocalMediaStatus.READY;
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();
  const setStoredVideoControl = useCallback(
    (enabled: boolean) =>
      setStoredAudioVideoSettings((prev) => ({
        ...prev,
        isVideoEnabled: enabled,
      })),
    [setStoredAudioVideoSettings],
  );

  return (
    <VideoControl
      isEnabled={isEnabled}
      loading={isLoading}
      onToggle={async (enable) => {
        if (enable) {
          if (await startVideo()) {
            setStoredVideoControl(true);
            return;
          }
          toast.error(
            <Text>
              {t('telehealth-core:controls.yourBrowserDoesntHaveAccessToYourCamera')}{' '}
              <NoAccessButton
                onClick={() => {
                  toast.close(NO_ACCESS_TOAST_KEY);
                  openHelpModal?.();
                }}
              />
            </Text>,
            { key: NO_ACCESS_TOAST_KEY, timeout: secondsToMilliseconds(20) },
          );
          return;
        }
        if (await stopVideo()) {
          setStoredVideoControl(false);
          return;
        }
        toast.error(t('telehealth-core:controls.anUnexpectedErrorOccurredWhileTryingToDisableYourCamera'));
      }}
    />
  );
}
